import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const DEFAULT_FEATURES = [
  { icon: '🤖', title: 'AI That Supports, Not Replaces', desc: 'Transparent AI that informs and orients your team — reducing admin burden without making hidden decisions. Your staff stays in control.' },
  { icon: '☁️', title: 'Cloud-Native Architecture', desc: 'No servers to maintain. No VPNs to manage. Access from anywhere, scale instantly, and never worry about backups or updates again.' },
  { icon: '🏢', title: 'Built for Multi-Location', desc: 'Enterprise controls with practice-level flexibility. Centralized reporting, standardized workflows, and location-specific configurations — all in one system.' },
  { icon: '📊', title: 'RevTech Suite Built In', desc: 'InsightFlow, Denial IQ, ClaimVision, Payer IQ, and Recon IQ — all integrated natively. No bolted-on analytics. No separate logins.' },
  { icon: '🔗', title: 'NDS DataVault Integration', desc: 'Real-time data flows into the NDS data platform. Enterprise analytics across your entire organization from day one.' },
  { icon: '🔌', title: 'Open API', desc: 'Connect to clearinghouses, imaging systems, patient communication tools, and any third-party system through a modern REST API.' },
];

const DEFAULT_MODULES = [
  { icon: '📋', name: 'Scheduling', desc: 'Intelligent appointment management with multi-provider, multi-location support' },
  { icon: '👤', name: 'Patients', desc: 'Comprehensive patient records with demographics, insurance, and clinical history' },
  { icon: '🦷', name: 'Clinical', desc: 'Treatment plans, charting, perio, ortho, imaging integration, and clinical notes' },
  { icon: '💰', name: 'Billing', desc: 'Claims submission, ERA/EOB processing, payment posting, and AR management' },
  { icon: '📄', name: 'Pre-Auth & Appeals', desc: 'Automated pre-authorization workflows and denial appeal management' },
  { icon: '📊', name: 'Reports', desc: 'Financial, production, and operational reporting with real-time dashboards' },
  { icon: '💬', name: 'Communications', desc: 'Patient messaging, referrals, prescriptions, and lab case tracking' },
  { icon: '🏥', name: 'Multi-Location', desc: 'Centralized management with location-specific configurations and RBAC' },
];

const DEFAULT_PRODUCTS = [
  { name: 'InsightFlow', desc: 'Enterprise analytics and visualization. Executive, regional, and practice-level dashboards with 70+ KPIs.', color: '#1976d2' },
  { name: 'Denial IQ', desc: 'Intelligent denial management. Root cause analysis, CARC proficiency tracking, and appeal automation.', color: '#f44336' },
  { name: 'ClaimVision', desc: 'Full claim lifecycle tracking from submission through payment with real-time status visibility.', color: '#ff9800' },
  { name: 'Payer IQ', desc: "Contract intelligence and benchmarking. Know exactly what every payer owes you and where you're leaving money.", color: '#4caf50' },
  { name: 'Recon IQ', desc: 'Payment reconciliation and EOB automation. Match payments to claims, flag underpayments, eliminate manual posting.', color: '#9c27b0' },
];

export default function HomePage() {
  const cmsPage = useCmsPage('xentrios', 'home');
  const heroHeadline = c(cmsPage, 'hero', 'headline', "Your practice management system\nshouldn't hold you back.");
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Xentrios is a cloud-native dental PMS built from the ground up for DSOs. AI-enabled to reduce admin burden on clinical staff. Ships with the full RevTech product suite — InsightFlow, Denial IQ, ClaimVision, Payer IQ, and Recon IQ — integrated out of the box.');
  const heroCta = c(cmsPage, 'hero', 'cta_text', 'Request a Demo');
  const features = c(cmsPage, 'features', 'items', DEFAULT_FEATURES);
  const modules = c(cmsPage, 'modules', 'items', DEFAULT_MODULES);
  const products = c(cmsPage, 'benefits', 'items', DEFAULT_PRODUCTS);
  return (
    <div>
      <Helmet><title>Xentrios — Next-Generation Dental PMS for DSOs</title></Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">The PMS built for modern dentistry</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>{heroCta}</Link>
            <Link to="/features" className="xen-btn xen-btn-outline" style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}>See Features</Link>
          </div>
        </div>
      </section>

      {/* Why Xentrios */}
      <section className="xen-section xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Why Xentrios</p>
          <h2>Legacy PMS systems weren't built for this.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            You're running a multi-location DSO on software designed for a single practice in 2005.
            Xentrios is built for how dental organizations actually operate today.
          </p>
          <div className="xen-features">
            {features.map(f => (
              <div key={f.title} className="xen-feature">
                <div className="xen-feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules overview */}
      <section className="xen-section xen-section-dark">
        <div className="xen-container">
          <div className="xen-two-col">
            <div>
              <p className="xen-section-eyebrow" style={{ color: 'var(--xen-accent-light)' }}>Complete Platform</p>
              <h2 style={{ color: '#fff' }}>Everything your practice needs. Nothing it doesn't.</h2>
              <p className="xen-section-subtitle">
                Xentrios covers the full practice workflow — from patient scheduling through claim reconciliation.
                Every module is designed to work together, not bolted on as an afterthought.
              </p>
            </div>
            <div className="xen-modules">
              {modules.map(m => (
                <div key={m.name} className="xen-module">
                  <div className="xen-module-icon">{m.icon}</div>
                  <div><h4>{m.name}</h4><p>{m.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Built-in RevTech */}
      <section className="xen-section xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Integrated Intelligence</p>
          <h2>The full RevTech suite. Built in. Not bolted on.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Every Xentrios deployment includes the complete RevTech Partners product suite — five RCM intelligence
            tools that turn your practice data into revenue recovery and operational insight.
          </p>
          <div className="xen-features">
            {products.map(p => (
              <div key={p.name} className="xen-feature" style={{ borderTop: `3px solid ${p.color}` }}>
                <h3 style={{ color: p.color }}>{p.name}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark" style={{ textAlign: 'center', padding: '4rem 0' }}>
        <div className="xen-container">
          <h2 style={{ color: '#fff', marginBottom: '1rem' }}>Ready to see Xentrios in action?</h2>
          <p style={{ color: 'var(--xen-gray)', marginBottom: '2rem', maxWidth: 500, margin: '0 auto 2rem' }}>
            30-minute personalized demo. See how Xentrios handles your specific workflows,
            integrations, and reporting needs.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.85rem 2.5rem', fontSize: '1rem' }}>Request a Demo</Link>
        </div>
      </section>
    </div>
  );
}
